<template>
  <CCard>
    <CCardHeader>
      Router IP Addresses
    </CCardHeader>
    <CCardBody>
      <CRow class="mb-3">
        <CCol sm="5">
          <div class="form-group">
            <label class="form-label">Router</label>
            <SearchSelect :options="routerOptions" v-model="selectedRouter" @change="onRouterChange" />
          </div>
        </CCol>
        <CCol sm="7" class="d-flex align-items-end">
          <CButtonGroup>
            <CButton
              color="info"
              square
              @click="syncIpAddresses"
              :disabled="!selectedRouter"
            >
              Sync
            </CButton>
            <CButton
              color="warning"
              square
              @click="loadUnmanaged"
              :disabled="!selectedRouter"
            >
              Claim Unmanaged
            </CButton>
            <CButton
              color="success"
              square
              @click="openAddModal"
              :disabled="!selectedRouter"
            >
              Add
            </CButton>
          </CButtonGroup>
        </CCol>
      </CRow>
      <CDataTable
        v-if="ipAddresses.length"
        hover
        small
        fixed
        table-filter
        sorter
        items-per-page-select
        :items-per-page="15"
        :fields="fields"
        :items="ipAddresses"
        :noItemsView="{ noItems: 'No Item~' }"
        pagination
      >
        <template #family="{item}">
          <td class="py-2">
            <CBadge :color="item.family === 'ipv6' ? 'info' : 'secondary'">
              {{ item.family === 'ipv6' ? 'IPv6' : 'IPv4' }}
            </CBadge>
          </td>
        </template>
        <template #status="{item}">
          <td class="py-2">
            <CBadge v-if="item.interface_name && item.interface_name.indexOf('-tunnel-managed-') >= 0" color="dark">Tunnel</CBadge>
            <CBadge v-else color="success">Managed</CBadge>
          </td>
        </template>
        <template #action="{item}">
          <td class="py-2">
            <CButtonGroup>
              <CButton
                color="primary"
                square
                size="sm"
                @click="openEditModal(item)"
              >
                Edit
              </CButton>
              <CButton
                color="warning"
                square
                size="sm"
                @click="repushIpAddress(item)"
              >
                Repush
              </CButton>
              <CButton
                color="info"
                square
                size="sm"
                @click="openConvertModal(item)"
              >
                转互联
              </CButton>
              <CButton
                color="danger"
                square
                size="sm"
                @click="deleteIpAddress(item)"
              >
                Delete
              </CButton>
            </CButtonGroup>
          </td>
        </template>
      </CDataTable>
      <CAlert v-else-if="selectedRouter" color="secondary" fade>
        没有纳管的地址。点 "Claim Unmanaged" 从路由器上纳管已有地址，或 "Add" 新增。
      </CAlert>
    </CCardBody>
    <CToaster :autohide="3000">
      <template v-for="toast in fixedToasts">
        <CToast
          :key="'toast' + toast"
          :show="true"
          :color="alertLevel"
          header="Alert"
        >
          {{ returnMsg }}
        </CToast>
      </template>
    </CToaster>
    <CModal
      title="Message"
      color="primary"
      :show.sync="requestMsgModal"
    >
      {{ requestMsg }}
      <template #footer>
        <CButton @click="requestMsgModal=false" color="primary">Close</CButton>
      </template>
    </CModal>

    <!-- Unmanaged IPs -->
    <CModal
      title="Unmanaged IP Addresses (实时查询)"
      color="warning"
      :show.sync="unmanagedModal"
      size="lg"
    >
      <CAlert v-if="!unmanagedList.length && !unmanagedLoading" color="success" fade>
        路由器上没有未纳管的地址。
      </CAlert>
      <CDataTable
        v-if="unmanagedList.length"
        small
        fixed
        sorter
        :items-per-page="10"
        :fields="unmanagedFields"
        :items="unmanagedList"
        :noItemsView="{ noItems: 'No Item~' }"
        pagination
      >
        <template #family="{item}">
          <td class="py-2">
            <CBadge :color="item.family === 'ipv6' ? 'info' : 'secondary'">
              {{ item.family === 'ipv6' ? 'IPv6' : 'IPv4' }}
            </CBadge>
          </td>
        </template>
        <template #action="{item}">
          <td class="py-2">
            <CButton
              color="success"
              square
              size="sm"
              @click="claimUnmanaged(item)"
            >
              Claim
            </CButton>
          </td>
        </template>
      </CDataTable>
      <template #footer>
        <CButton @click="unmanagedModal=false" color="primary">Close</CButton>
      </template>
    </CModal>

    <!-- Add IP -->
    <CModal
      title="Add IP Address"
      color="success"
      :show.sync="addModal"
    >
      <CForm>
        <div class="form-group">
          <label class="form-label">Interface</label>
          <SearchSelect :options="interfaceOptions" v-model="addForm.interface_name" />
        </div>
        <CInput
          label="Address"
          v-model="addForm.address"
          placeholder="1.2.3.4/24 或 fd00::1/64"
        />
        <CInput
          label="Remark"
          v-model="addForm.remark"
        />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="addModal=false">Cancel</CButton>
          <CButton color="success" @click="addIpAddress">Add</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Edit IP -->
    <CModal
      title="Edit IP Address"
      color="primary"
      :show.sync="editModal"
    >
      <CForm>
        <div class="form-group">
          <label class="form-label">Interface</label>
          <SearchSelect :options="editInterfaceOptions" v-model="editForm.interface_name" />
        </div>
        <CInput
          label="Address"
          v-model="editForm.address"
          placeholder="1.2.3.4/24"
        />
        <CInput
          label="Remark"
          v-model="editForm.remark"
        />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="editModal=false">Cancel</CButton>
          <CButton color="success" @click="editIpAddress">Save</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 转互联 IP Modal -->
    <CModal title="转化为互联 IP 分配" color="info" :show.sync="convertModal.show">
      <CForm v-if="convertModal.item">
        <CInput label="本记录" :value="convertModal.item.address + ' @ ' + convertModal.item.interface_name" disabled />
        <CSelect
          label="本端位置"
          v-model="convertModal.this_is_a"
          :options="[
            { value: 'a', label: 'A 端（本路由器拿 .1）' },
            { value: 'b', label: 'B 端（本路由器拿 .2）' }
          ]"
        />
        <CSelect
          label="对端类型"
          v-model="convertModal.peer_type"
          :options="[
            { value: 'router', label: 'Router' },
            { value: 'cmdb', label: 'CMDB 设备' }
          ]"
        />
        <CSelect
          v-if="convertModal.peer_type === 'router'"
          label="对端 Router"
          v-model="convertModal.peer_id"
          :options="convertRouterOptions"
          placeholder="请选择"
        />
        <CSelect
          v-else
          label="对端 CMDB 设备"
          v-model="convertModal.peer_id"
          :options="convertCmdbOptions"
          placeholder="请选择"
        />
        <CInput label="备注" v-model="convertModal.remark" placeholder="可选" />
        <CAlert color="info" fade>
          将按该 IP 推导所属 /30 网段，自动匹配（或创建）网段池并生成互联 IP 分配记录，供 Tunnel 创建时引用。
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="convertModal.show = false">取消</CButton>
          <CButton color="success" :disabled="convertModal.loading" @click="submitConvert">{{ convertModal.loading ? '转换中...' : '转换' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </CCard>
</template>

<script>
const fields = [
  { key: "address", label: "Address" },
  { key: "interface_name", label: "Interface" },
  { key: "family", label: "Family" },
  { key: "status", label: "Status", sorter: false, filter: false },
  { key: "remark", label: "Remark" },
  {
    key: "action",
    label: "Action",
    sorter: false,
    filter: false,
    _style: "width: 15%"
  }
];

const unmanagedFields = [
  { key: "address", label: "Address" },
  { key: "interface", label: "Interface" },
  { key: "family", label: "Family" },
  {
    key: "action",
    label: "Action",
    sorter: false,
    filter: false,
    _style: "width: 12%"
  }
];

export default {
  name: "IpAddressManagement",
  data() {
    return {
      fixedToasts: 0,
      returnMsg: "",
      alertLevel: "success",
      requestMsgModal: false,
      requestMsg: null,
      fields,
      unmanagedFields,
      routers: [],
      interfaces: [],
      selectedRouter: null,
      ipAddresses: [],
      unmanagedModal: false,
      unmanagedList: [],
      unmanagedLoading: false,
      addModal: false,
      addForm: {
        interface_name: null,
        address: "",
        remark: ""
      },
      editModal: false,
      editForm: {
        id: null,
        interface_name: null,
        address: '',
        remark: ''
      },
      convertModal: {
        show: false,
        loading: false,
        item: null,
        this_is_a: 'a',
        peer_type: 'router',
        peer_id: '',
        remark: ''
      },
      cmdbDevices: []
    };
  },
  computed: {
    routerOptions: function() {
      let options = [{ label: "Select a router" }];
      this.routers.map(item => {
        options.push({ value: item.id, label: item.name + "(" + item.host + ")" });
      });
      return options;
    },
    interfaceOptions: function() {
      let options = [{ label: "Select interface" }];
      this.interfaces.map(item => {
        options.push({ value: item.interface_name, label: item.interface_name });
      });
      return options;
    },
    editInterfaceOptions: function() {
      let options = [];
      this.interfaces.map(item => {
        options.push({ value: item.interface_name, label: item.interface_name });
      });
      return options;
    },
    convertRouterOptions: function() {
      return this.routers
        .filter(r => r.id != this.selectedRouter)
        .map(r => ({ value: r.id, label: r.name }));
    },
    convertCmdbOptions: function() {
      return this.cmdbDevices.map(d => ({ value: d.id, label: `${d.name} (#${d.id})` }));
    }
  },
  mounted: function() {
    this.$axios.get("user/router", {})
      .then((response) => {
        if (response.data.code === 200) {
          this.routers = response.data.userRouter;
        }
      });
    this.$axios.get("user/cmdb/device/list", {})
      .then((response) => {
        if (response.data.code === 200) {
          this.cmdbDevices = response.data.devices;
        }
      });
  },
  methods: {
    onRouterChange: function() {
      this.ipAddresses = [];
      this.interfaces = [];
      if (!this.selectedRouter) return;
      this.loadIpAddresses();
      this.loadInterfaces();
    },
    loadIpAddresses: function() {
      this.$axios.get("user/ip-address/list", {
        params: { router_id: this.selectedRouter }
      })
        .then((response) => {
          if (response.data.code === 200) {
            this.ipAddresses = response.data.ip_addresses;
          }
        });
    },
    loadInterfaces: function() {
      this.$axios.get("user/interface/list", {
        params: { router_id: this.selectedRouter }
      })
        .then((response) => {
          if (response.data.code === 200) {
            this.interfaces = response.data.interfaces;
          }
        });
    },
    syncIpAddresses: function() {
      this.$axios.get("user/ip-address/router/" + this.selectedRouter + "/sync", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.loadIpAddresses();
          } else {
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    loadUnmanaged: function() {
      this.unmanagedList = [];
      this.unmanagedLoading = true;
      this.unmanagedModal = true;
      this.$axios.get("user/ip-address/router/" + this.selectedRouter + "/unmanaged", {})
        .then((response) => {
          this.unmanagedLoading = false;
          if (response.data.code === 200) {
            this.unmanagedList = response.data.unmanaged;
          } else {
            this.unmanagedModal = false;
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    claimUnmanaged: function(item) {
      this.$axios.post("user/ip-address/claim", {
        router_id: this.selectedRouter,
        interface_name: item.interface,
        address: item.address
      })
        .then((response) => {
          if (response.data.code === 200) {
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.unmanagedList = this.unmanagedList.filter(
              i => !(i.interface === item.interface && i.address === item.address)
            );
            this.loadIpAddresses();
          } else {
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    openAddModal: function() {
      this.addForm = { interface_name: null, address: "", remark: "" };
      this.addModal = true;
    },
    addIpAddress: function() {
      if (!this.addForm.interface_name || !this.addForm.address) {
        this.requestMsg = "Interface and address can not be null";
        this.requestMsgModal = true;
        return;
      }
      this.$axios.post("user/ip-address/add", {
        router_id: this.selectedRouter,
        interface_name: this.addForm.interface_name,
        address: this.addForm.address,
        remark: this.addForm.remark
      })
        .then((response) => {
          if (response.data.code === 200) {
            this.addModal = false;
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.loadIpAddresses();
          } else {
            this.addModal = false;
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    openEditModal: function(item) {
      this.editForm = {
        id: item.id,
        interface_name: item.interface_name,
        address: item.address,
        remark: item.remark || ''
      };
      this.editModal = true;
    },
    editIpAddress: function() {
      this.$axios.post("user/ip-address/" + this.editForm.id + "/edit", {
        interface_name: this.editForm.interface_name,
        address: this.editForm.address,
        remark: this.editForm.remark
      })
        .then((response) => {
          if (response.data.code === 200) {
            this.editModal = false;
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.loadIpAddresses();
          } else {
            this.editModal = false;
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    deleteIpAddress: function(item) {
      if (!confirm("Delete address " + item.address + " on " + item.interface_name + "?")) return;
      this.$axios.delete("user/ip-address/" + item.id + "/delete", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.loadIpAddresses();
          } else {
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    repushIpAddress: function(item) {
      if (!confirm("重新下发 " + item.address + " @ " + item.interface_name + " 到路由器？")) return;
      this.$axios.post("user/ip-address/" + item.id + "/repush", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.loadIpAddresses();
          } else {
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    openConvertModal: function(item) {
      this.convertModal.item = item;
      this.convertModal.this_is_a = 'a';
      this.convertModal.peer_type = 'router';
      this.convertModal.peer_id = '';
      this.convertModal.remark = '';
      this.convertModal.show = true;
    },
    submitConvert: function() {
      if (!this.convertModal.peer_id) {
        this.requestMsg = "请选择对端";
        this.requestMsgModal = true;
        return;
      }
      this.convertModal.loading = true;
      this.$axios.post("user/link-ip/allocation/convert", {
        ip_address_id: this.convertModal.item.id,
        peer_type: this.convertModal.peer_type,
        peer_id: this.convertModal.peer_id,
        this_is_a: this.convertModal.this_is_a === 'a',
        remark: this.convertModal.remark
      })
        .then((response) => {
          this.convertModal.loading = false;
          if (response.data.code === 200) {
            this.alertLevel = "success";
            this.returnMsg = "已转化: " + response.data.data.cidr +
              " (" + response.data.data.a_ip + " / " + response.data.data.b_ip + ")";
            this.fixedToasts++;
            this.convertModal.show = false;
          } else {
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
  }
};
</script>
